import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Chip,
  Grid,
  Stack,
  Paper,
  Autocomplete
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { CgMenu } from "react-icons/cg";
import { keyframes } from "@emotion/react";
import { fetchFilterData } from "../services/api";

// --- COSTANTI E UTILS ---
const legalityMapping = {
  standard: 0, future: 1, historic: 2, timeless: 3, gladiator: 4,
  pioneer: 5, explorer: 6, modern: 7, legacy: 8, pauper: 9,
  vintage: 10, penny: 11, commander: 12, oathbreaker: 13,
  standardbrawl: 14, brawl: 15, alchemy: 16, paupercommander: 17,
  duel: 18, oldschool: 19, premodern: 20, predh: 21,
};

const scroll = keyframes`
  0%   { transform: translateX(100%); }
  100% { transform: translateX(-100%); }`;

const isValidPowerToughness = (value) => {
  if (value === '') return true;
  let ptRegex = /^([0-9]|[1-2][0-9]|30|\*|1\+\*|\*\+1|2\+\*|\+[1-4])$/;
  return ptRegex.test(value);
};

const isValidManaValue = (value) => {
  if (value === '') return true;
  const manaRegex = /^([0-9]|1[0-9]|20|X|x)$/;
  return manaRegex.test(value);
};

const normalizeString = (str) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const createColorBitmask = (selectedColors) => {
  if (selectedColors.length === 0) return null;
  if (selectedColors.includes("Colorless")) return "0,0,0,0,0,1";
  const colorOrder = ["Black", "Green", "Red", "Blue", "White"];
  const bitmaskArray = colorOrder.map((color) =>
    selectedColors.includes(color) ? "1" : "0"
  );
  bitmaskArray.push("0");
  return bitmaskArray.join(",");
};

// --- COMPONENTE ---
const SearchForm = ({ onSearch, isPending, onResetResults, initialCardName }) => {
  const initialFilters = {
    cardName: "",
    legality: "",
    type: "",
    subType: "",
    isLegendary: false,
    colors: [],
    manaValue: "",
    manaOperator: "=",
    power: "",
    toughness: "",
    keywords: [],
    colorModality: "exact",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [currentKeyword, setCurrentKeyword] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    names: [], legalities: [], types: [], subTypes: [], keywords: [],
  });

  useEffect(() => {
    if (initialCardName) {
      setFilters(prev => ({ ...prev, cardName: initialCardName }));
    }
  }, [initialCardName]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [names, legalities, types, subTypes, keywords] = await Promise.all([
          fetchFilterData("name_cards"),
          fetchFilterData("legalities"),
          fetchFilterData("type"),
          fetchFilterData("sub_type"),
          fetchFilterData("keyword"),
        ]);
        setDropdownData({
          names: names.sort(),
          legalities: legalities.sort(),
          types: types.sort(),
          subTypes: subTypes.sort(),
          keywords: keywords.sort(),
        });
      } catch (error) {
        console.error("Failed to load filter data:", error);
      }
    };
    loadData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleColorChange = (e) => {
    const { value, checked } = e.target;
    let newColors = [...filters.colors];
    if (checked) {
      if (value === "Colorless") newColors = ["Colorless"];
      else {
        newColors.push(value);
        newColors = newColors.filter(color => color !== "Colorless");
      }
    } else {
      newColors = newColors.filter(color => color !== value);
    }
    setFilters(prev => ({ ...prev, colors: newColors }));
  };

  const handleBlurValidation = (e) => {
    const { name, value } = e.target;
    if (name === 'power' || name === 'toughness') {
      if (value !== '' && !isValidPowerToughness(value)) {
        alert("Value invalid! Insert a number from 0 to 20 or a special character permitted like 1+* or *");
        setFilters(prev => ({ ...prev, [name]: '' }));
      }
    }
    if (name === 'manaValue') {
      if (value !== '' && !isValidManaValue(value)) {
        alert("Mana Cost Invalid! Insert a number from 0 to 20 or 'X'.");
        setFilters(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const handleAddKeyword = () => {
    if (currentKeyword && !filters.keywords.includes(currentKeyword)) {
      setFilters((prev) => ({
        ...prev,
        keywords: [...prev.keywords, currentKeyword],
      }));
      setCurrentKeyword("");
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    setFilters((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keywordToRemove),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isPending) return;
    let legalityBitmask = null;
    if (filters.legality) {
      const position = legalityMapping[filters.legality.toLowerCase()];
      if (position !== undefined) legalityBitmask = 1 << position;
    }

    const activeFilters = {
      legendary: filters.isLegendary ? 1 : null,
      type_line: filters.type || null,
      subtype: filters.subType || null,
      power: filters.power || null,
      toughness: filters.toughness || null,
      legalities: legalityBitmask,
      keywords: filters.keywords.length > 0 ? filters.keywords.join(", ") : null,
      cmc: filters.manaValue || null,
      cmc_condition: filters.manaValue ? filters.manaOperator : null,
      color_identity: createColorBitmask(filters.colors),
      color_modality: filters.colors.length > 0 ? (filters.colorModality === "at_most" ? 1 : null) : null,
    };

    Object.keys(activeFilters).forEach(
      (key) => activeFilters[key] == null && delete activeFilters[key]
    );

    onSearch({ name_card: filters.cardName, filters: activeFilters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setCurrentKeyword("");
    onResetResults();
    setIsFiltersOpen(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box component="img" src="/sfondo.png" alt="ӔTHER SEARCH" sx={{ maxWidth: "300px" }} />
      <Typography variant="h5" sx={{
        background: "linear-gradient(to right, #8871ed, #9d45c9, #8871ed)",
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        fontWeight: "bold", mt: 2,
      }}>
        ӔTHER SEARCH
      </Typography>

      <Typography variant="h1" sx={{ color: "transparent", fontWeight: "light", mt: 1, fontSize: "0.1rem", lineHeight: 0.1 }}>
        Search any Magic: The Gathering card to find replacements, upgrades, and cards that play a similar role in your deck.
        Best MTG Search Engine: Find Similar, Alternative & Replacement Cards for Your Magic: The Gathering Decks
        Discover similar Magic: The Gathering cards. Find replacements, upgrades, and cards that play the same role in MTG deckbuilding.
      </Typography>
      <Typography variant="h2" sx={{ textAlign: "center", color: "#b88aff", fontWeight: "light", mt: 1, fontSize: "1.2rem", lineHeight: 1.2 }}>
        Search for Similar, Alternative or Replacement MTG Cards
      </Typography>
      <Typography
        variant="body2"
        gutterBottom
        sx={{
          textAlign: "center",
          color: "#908e91",
          opacity: 0.7,
          fontSize: "0,8rem",
          fontStyle: "italic",
          mt: 2,
          mb: 1
        }}
      >
        TCGplayer affiliated links
      </Typography>
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          backgroundColor: "rgba(75, 18, 131, 0.35)",
          color: "#b88aff",
          py: 1,
          position: "relative",
          whiteSpace: "nowrap",
          my: 2,
          "&:hover .scroll-text": {
            animationPlayState: "paused"
          }
        }}
      >
        <Typography
          className="scroll-text"
          variant="body2"
          sx={{
            fontWeight: "bold",
            display: "inline-block",
            animation: `${scroll} 18s linear infinite`,
            paddingLeft: "100%" 
          }}
        >
          Monthly Search Update - Some card prices do not guarantee accuracy
        </Typography>
      </Box>

      <Paper component="form" onSubmit={handleSubmit} 
      onKeyDown={(e) => {
        if (e.key === "Enter" && (isPending)) {
          e.preventDefault();
        }
      }} 
      sx={{
        mb: 4, p: 4, borderRadius: 3,
        backgroundImage: "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)),url('/liliana_bg.jpg')",
        backgroundSize: "cover", backgroundPosition: "center", boxShadow: "0 0 20px #5c4eb9",
        display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: "1200px", color: "#f2f2f2", textAlign: "center"
      }}>
        <Typography variant="h6" gutterBottom sx={{ color: "#b88aff" }}>Source Card</Typography>

        <Autocomplete
          freeSolo
          disableClearable
          filterOptions={(options, state) => {
            const normalizedInput = normalizeString(state.inputValue);
            return options
              .filter((option) => normalizeString(option).includes(normalizedInput))
              .slice(0, 20);
          }}
          options={filters.cardName.length >= 3 ? dropdownData.names : []}
          value={filters.cardName}
          onInputChange={(event, newValue) => {
            setFilters(prev => ({ ...prev, cardName: newValue }));
          }}
          disableListWrap
          renderInput={(params) => (
            <TextField
              {...params}
              label="Card Name"
              margin="normal"
              required
              sx={{
                width: "300px",
                input: { color: "#f2f2f2" },
                label: { color: "#b88aff" },
                "& label.Mui-focused": { color: "#b88aff" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#5c4eb9" },
                  "&:hover fieldset": { borderColor: "#b88aff" },
                  "&.Mui-focused fieldset": { borderColor: "#b88aff" },
                },
              }}
            />
          )}
        />

        <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
          <Typography variant="subtitle1" sx={{ color: "#f2f2f2", mr: 1 }}>Filters</Typography>
          <Button variant="outlined" onClick={() => setIsFiltersOpen(!isFiltersOpen)} sx={{ color: "#b88aff", borderColor: "#5c4eb9", "&:hover": { backgroundColor: "#5c4eb9", color: "#fff" } }}>
            <CgMenu />
          </Button>
        </Box>

        {isFiltersOpen && (
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={dropdownData.legalities}
                value={filters.legality}
                onChange={(e, v) => setFilters(p => ({ ...p, legality: v || '' }))}
                renderInput={(params) => <TextField {...params} label="Legality" />}
                sx={autocompleteStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={dropdownData.types}
                value={filters.type}
                onChange={(e, v) => setFilters(p => ({ ...p, type: v || '' }))}
                renderInput={(params) => <TextField {...params} label="Type" />}
                sx={autocompleteStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={dropdownData.subTypes}
                value={filters.subType}
                onChange={(e, v) => setFilters(p => ({ ...p, subType: v || '' }))}
                renderInput={(params) => <TextField {...params} label="Subtype" />}
                sx={autocompleteStyle}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="Mana Cost" value={filters.manaValue} name="manaValue" onBlur={handleBlurValidation} onChange={handleChange} placeholder="e.g. 3, X"
                InputProps={{
                  endAdornment: (
                    <Select name="manaOperator" value={filters.manaOperator} onChange={handleChange} sx={{ color: "#f2f2f2", "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "& .MuiSvgIcon-root": { color: "#b88aff" } }}>
                      <MenuItem value="=">=</MenuItem>
                      <MenuItem value="<">&lt;</MenuItem>
                      <MenuItem value=">">&gt;</MenuItem>
                    </Select>
                  )
                }}
                sx={textFieldStyle}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Power" name="power" value={filters.power} onChange={handleChange} onBlur={handleBlurValidation} placeholder="e.g. 2, *" sx={textFieldStyle} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Toughness" name="toughness" value={filters.toughness} onChange={handleChange} onBlur={handleBlurValidation} placeholder="e.g. 2, *" sx={textFieldStyle} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Autocomplete options={dropdownData.keywords} value={currentKeyword} onChange={(e, v) => setCurrentKeyword(v || '')}
                  renderInput={(params) => <TextField {...params} label="Keywords" />} sx={{ flexGrow: 1, ...autocompleteStyle }}
                />
                <Button variant="outlined" onClick={handleAddKeyword} sx={{ color: "#b88aff", borderColor: "#5c4eb9" }}><AddIcon /></Button>
              </Box>
              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {filters.keywords.map(k => <Chip key={k} label={k} onDelete={() => handleRemoveKeyword(k)} sx={{ bgcolor: "#222", color: "#b88aff" }} />)}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={<Checkbox checked={filters.isLegendary} onChange={handleChange} name="isLegendary" sx={{ color: "#b88aff", "&.Mui-checked": { color: "#b88aff" } }} />}
                label={<Typography sx={{ color: "#f2f2f2" }}>Legendary</Typography>}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: "#b88aff", mb: 1 }}>Color Identity</Typography>
              <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                <Select name="colorModality" value={filters.colorModality} onChange={handleChange} sx={{ color: "#b88aff", borderColor: "#5c4eb9", height: 40 }}>
                  <MenuItem value="exact">Exact</MenuItem>
                  <MenuItem value="at_most">At Most</MenuItem>
                </Select>
                <Box>
                  {["White", "Blue", "Black", "Red", "Green", "Colorless"].map((c) => (
                    <FormControlLabel key={c} control={<Checkbox size="small" value={c} checked={filters.colors.includes(c)} onChange={handleColorChange} sx={{ color: "#b88aff" }} />} label={c} sx={{ color: "#f2f2f2" }} />
                  ))}
                </Box>
              </Stack>
            </Grid>
          </Grid>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          <Button type="submit" variant="contained" disabled={isPending} sx={{ backgroundColor: "#5c4eb9", color: "#fff", px: 4, "&:hover": { backgroundColor: "#b88aff" } }}>
            {isPending ? "Searching..." : "Search Cards"}
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ color: "#b88aff", borderColor: "#5c4eb9", px: 4 }}>Reset</Button>
        </Stack>
      </Paper>
    </Box>
  );
};

const autocompleteStyle = {
  "& .MuiInputLabel-root": { color: "#b88aff" },
  "& .MuiOutlinedInput-root": {
    color: "#f2f2f2",
    "& fieldset": { borderColor: "#5c4eb9" },
    "&:hover fieldset": { borderColor: "#b88aff" },
    "&.Mui-focused fieldset": { borderColor: "#b88aff" },
  },
};

const textFieldStyle = {
  width: "100%",
  "& label.Mui-focused": { color: "#b88aff" },
  "& .MuiInputLabel-root": { color: "#b88aff" },
  "& .MuiOutlinedInput-root": {
    color: "#f2f2f2",
    "& fieldset": { borderColor: "#5c4eb9" },
    "&:hover fieldset": { borderColor: "#b88aff" },
    "&.Mui-focused fieldset": { borderColor: "#b88aff" }
  }
};

export default SearchForm;